/**
 * Подбор товаров и услуг в договор — общий для «добавить в договор» и создания договора. Три способа:
 * «Из сердечек» клиента (`GET /api/chats/{id}/client`: понравилось в салоне + понравилось с серией),
 * «По серии» — предпросмотр карточки серии (место, брони против дат договора, цены проката) и
 * «Услуга» из справочника CRM. Отмеченное — списком с ценой (подставлена, правится) и днями проката
 * (по умолчанию пусто). Сам ничего не пишет: отдаёт выбор через `onChange`. Артикул не показываем.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useRef, useState } from "react";

import { clientQuery } from "@/components/chat/ClientTab";
import { numCls } from "@/components/contract/ui";
import type { ClientInterest } from "@/lib/clientChats";
import { type PickedItem, DAYS_MAX, PRICE_MAX, contractServicesQuery, digits } from "@/lib/contract";
import { day } from "@/lib/dealStage";
import { btn } from "@/lib/panelBtn";
import { type SerialCard, type SerialReserve, serialCardQuery } from "@/lib/serialCard";

type Entry = { key: number; item: PickedItem };
type Tab = "hearts" | "serial" | "service";

const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const box = "aspect-[3/4] w-full rounded-[6px] bg-black/5 object-cover";

/** Поле числа: только цифры, сверх предела — предел; пусто — `null`. */
const num = (s: string, max: number): number | null => {
  const t = digits(s).slice(0, 10);
  return t ? Math.min(Number(t), max) : null;
};
const d10 = (s: string | null | undefined) => (s ? s.slice(0, 10) : null);

/** Бронь другой сделки на даты договора; дат нет — пересечений не ищем. */
function overlaps(r: SerialReserve, start: string | null | undefined, end: string | null | undefined, dealId: number | null | undefined) {
  const [s, e, rs, re] = [d10(start), d10(end), d10(r.start), d10(r.end)];
  if (r.deal === dealId || !s || !e || !rs || !re) return false;
  return rs <= e && re >= s;
}

type Cand = { serial: number; name: string; size: string; price: number | null; photo: string; out: boolean };

/** Сердечки клиента с серией: понравилось в салоне первыми, затем «понравилось» с сайта и ЛК. */
function candidates(d: ClientInterest, exclude: Set<number>): Cand[] {
  const out = new Set(
    (d.liked ?? []).filter((l) => l.serial && d.crm && l.category?.startsWith("Платья") && !l.in_stock).map((l) => l.serial!),
  );
  const seen = new Map<number, Cand>();
  for (const h of d.salon?.liked ?? [])
    if (!seen.has(h.serial)) seen.set(h.serial, { serial: h.serial, name: h.name, size: h.size, price: h.price, photo: h.photo, out: out.has(h.serial) });
  for (const l of d.liked ?? [])
    if (l.serial && !seen.has(l.serial))
      seen.set(l.serial, { serial: l.serial, name: l.category || "Товар", size: l.size, price: l.price, photo: l.photo, out: out.has(l.serial) });
  return [...seen.values()].filter((c) => !exclude.has(c.serial));
}

function Hearts({ chatId, exclude, picked, onToggle }: { chatId: number; exclude: Set<number>; picked: Set<number>; onToggle: (c: Cand) => void }) {
  const q = useQuery(clientQuery(chatId));
  if (q.isPending) return <div className="text-fg-muted">Загружаю сердечки клиента…</div>;
  if (q.isError) return <div className="text-err">Сердечки не загрузились: {q.error.message}</div>;
  const list = candidates(q.data, exclude);
  if (list.length === 0) return <div className="text-fg-muted">Сердечек с серией нет (или все уже в договоре).</div>;
  return (
    <ul className="grid grid-cols-3 gap-1.5" data-annot="сердечки клиента для договора">
      {list.map((c) => {
        const on = picked.has(c.serial);
        return (
          <li key={c.serial} className="min-w-0">
            <button
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(c)}
              title={c.out ? "Нет в наличии — добавить всё равно можно" : undefined}
              className={`relative flex w-full flex-col gap-0.5 rounded-[var(--radius-ui)] border p-1 text-left text-[11.5px] leading-[15px] ${
                on ? "border-accent ring-1 ring-accent" : "border-border hover:border-border-strong"
              }`}
            >
              <span className={`relative block ${c.out ? "opacity-50 grayscale" : ""}`}>
                {c.photo ? <img src={c.photo} alt="" loading="lazy" className={box} /> : <span className={`block ${box}`} />}
              </span>
              {on && (
                <span className="absolute right-1.5 top-1.5 grid size-5 place-items-center rounded-full bg-accent text-white">
                  <Check size={12} aria-hidden />
                </span>
              )}
              <span className={`break-words ${c.out ? "text-fg-muted" : ""}`}>
                {c.name} · <span className="font-mono">{c.serial}</span>
              </span>
              <span className="text-fg-muted">{[c.size, c.price != null ? rub(c.price) : ""].filter(Boolean).join(" · ")}</span>
              {c.out && <span className="text-fg-muted">нет в наличии</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Preview({ card, rentStart, rentEnd, dealId, inContract, isPicked, onPick }: {
  card: SerialCard;
  rentStart?: string | null | undefined;
  rentEnd?: string | null | undefined;
  dealId?: number | null | undefined;
  inContract: boolean;
  isPicked: boolean;
  onPick: () => void;
}) {
  const prices = card.price.rent.filter((x): x is number => x != null);
  const reserves = card.reserves.filter((r) => r.deal !== dealId);
  const bad = reserves.filter((r) => overlaps(r, rentStart, rentEnd, dealId));
  return (
    <div className="flex gap-2.5 rounded-[var(--radius-ui)] border border-border bg-bg p-2" data-annot={`предпросмотр серии ${card.serial}`}>
      {card.photo ? (
        <img src={card.photo} alt="" loading="lazy" className="h-[84px] w-[60px] flex-none rounded-[var(--radius-ui)] object-cover" />
      ) : (
        <div className="grid h-[84px] w-[60px] flex-none place-items-center rounded-[var(--radius-ui)] bg-bg-subtle text-[11px] text-fg-faint">нет фото</div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="break-words font-medium">
          {card.name ?? "Товар"} · <span className="font-mono">{card.serial}</span>
        </div>
        <div className="text-fg-muted">
          {[card.size, card.place.city].filter(Boolean).join(" · ")}
          {card.size || card.place.city ? " · " : ""}
          <span className={card.available ? "text-ok" : "text-err"}>{card.available ? "в салоне" : "нет в наличии"}</span>
        </div>
        <div>
          <span className="text-fg-muted">Прокат:</span> {prices.length ? prices.map(rub).join(" / ") : "цены нет"}
        </div>
        {reserves.length === 0 ? (
          <div className="text-fg-muted">Других броней нет.</div>
        ) : (
          <ul className="flex flex-col">
            {reserves.map((r, i) => (
              <li key={`${r.deal}-${i}`} className={overlaps(r, rentStart, rentEnd, dealId) ? "font-medium text-err" : "text-fg-muted"}>
                бронь {r.deal} · {r.stage_name ?? r.stage} · {day(r.start)} – {day(r.end)}
                {r.city ? ` · ${r.city}` : ""}
              </li>
            ))}
          </ul>
        )}
        {bad.length > 0 && <div className="text-err">Пересекается с датами договора — добавить можно, но проверьте.</div>}
        <div className="mt-1">
          {inContract ? (
            <span className="text-fg-muted">Уже в договоре.</span>
          ) : isPicked ? (
            <span className="text-ok">Выбрано.</span>
          ) : (
            <button type="button" onClick={onPick} className={btn.outline}>
              Выбрать
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BySerial({ exclude, picked, rentStart, rentEnd, dealId, onPick }: {
  exclude: Set<number>;
  picked: Set<number>;
  rentStart?: string | null | undefined;
  rentEnd?: string | null | undefined;
  dealId?: number | null | undefined;
  onPick: (card: SerialCard) => void;
}) {
  const [input, setInput] = useState("");
  const [serial, setSerial] = useState<number | null>(null);
  const q = useQuery({ ...serialCardQuery(serial ?? 0), enabled: serial != null });
  // Пока ищется новая серия, `q.data` — прошлая (placeholder): её не показываем и выбрать не даём.
  const fresh = serial != null && q.data?.serial === serial && !q.isPlaceholderData;
  const find = () => {
    const n = num(input, Number.MAX_SAFE_INTEGER);
    setSerial(n && n > 0 ? n : null);
  };
  return (
    <div className="flex flex-col gap-2">
      <form className="flex items-center gap-2" onSubmit={(e) => (e.preventDefault(), find())}>
        <input className={`${numCls} w-32 font-mono`} inputMode="numeric" placeholder="серия" aria-label="Номер серии" value={input} onChange={(e) => setInput(e.target.value)} />
        <button type="submit" className={btn.quiet} disabled={!input.trim()}>
          Найти
        </button>
      </form>
      {serial != null && !fresh && !q.isError && <div className="text-fg-muted">Ищу серию {serial}…</div>}
      {serial != null && q.isError && <div className="text-err">Серия {serial}: {q.error.message}</div>}
      {fresh && q.data && (
        <Preview
          card={q.data}
          rentStart={rentStart}
          rentEnd={rentEnd}
          dealId={dealId}
          inContract={exclude.has(q.data.serial)}
          isPicked={picked.has(q.data.serial)}
          onPick={() => onPick(q.data)}
        />
      )}
    </div>
  );
}

function Service({ onPick }: { onPick: (id: number, name: string) => void }) {
  const q = useQuery(contractServicesQuery());
  if (q.isPending) return <div className="text-fg-muted">Загружаю услуги…</div>;
  if (q.isError) return <div className="text-err">Услуги не загрузились: {q.error.message}</div>;
  return (
    <select
      className={`${numCls} w-full`}
      value=""
      aria-label="Услуга"
      onChange={(e) => {
        const s = q.data.find((x) => String(x.id) === e.target.value);
        if (s) onPick(s.id, s.serviceName);
      }}
    >
      <option value="">— выберите услугу —</option>
      {q.data.map((s) => (
        <option key={s.id} value={s.id}>
          {s.serviceName}
        </option>
      ))}
    </select>
  );
}

export function ProductPicker({
  chatId,
  dealId,
  rentStart,
  rentEnd,
  exclude,
  initial = [],
  disabled = false,
  onChange,
}: {
  /** Чат клиента — для «Из сердечек»; нет — способа нет. */
  chatId: number | null;
  /** Бронь этой же сделки пересечением не считаем. */
  dealId?: number | null;
  rentStart?: string | null;
  rentEnd?: string | null;
  /** Серии, что уже живые в договоре. */
  exclude: number[];
  /** Выбор при открытии (после частичной ошибки добавления — то, что не добавилось). */
  initial?: PickedItem[];
  disabled?: boolean;
  onChange: (items: PickedItem[]) => void;
}) {
  const seq = useRef(initial.length);
  const [entries, setEntries] = useState<Entry[]>(() => initial.map((item, key) => ({ key, item })));
  const [tab, setTab] = useState<Tab>(chatId ? "hearts" : "serial");
  const ex = new Set(exclude);
  const pickedSerials = new Set(entries.flatMap((e) => (e.item.kind === "serial" ? [e.item.serial] : [])));

  const update = (next: Entry[]) => {
    setEntries(next);
    onChange(next.map((e) => e.item));
  };
  const add = (item: PickedItem) => update([...entries, { key: seq.current++, item }]);
  const remove = (key: number) => update(entries.filter((e) => e.key !== key));
  const patch = (key: number, p: Partial<{ price: number | null; days: number | null }>) =>
    update(entries.map((e) => (e.key === key ? { ...e, item: { ...e.item, ...p } as PickedItem } : e)));
  const toggleSerial = (serial: number, name: string, price: number | null) => {
    const has = entries.find((e) => e.item.kind === "serial" && e.item.serial === serial);
    if (has) remove(has.key);
    else add({ kind: "serial", serial, name, price, days: null });
  };

  const tabs: [Tab, string][] = [
    ...(chatId ? ([["hearts", "Из сердечек"]] as [Tab, string][]) : []),
    ["serial", "По серии"],
    ["service", "Услуга"],
  ];
  return (
    <fieldset disabled={disabled} className="flex flex-col gap-2 text-[12.5px]" data-annot="подбор товаров в договор">
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Как добавить">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`h-7 rounded-full border px-2.5 text-[12px] ${tab === id ? "border-accent bg-accent text-white" : "border-border hover:border-border-strong hover:bg-bg-hover"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "hearts" && chatId && (
        <Hearts chatId={chatId} exclude={ex} picked={pickedSerials} onToggle={(c) => toggleSerial(c.serial, c.name, c.price)} />
      )}
      {tab === "serial" && (
        <BySerial
          exclude={ex}
          picked={pickedSerials}
          rentStart={rentStart}
          rentEnd={rentEnd}
          dealId={dealId}
          onPick={(card) => toggleSerial(card.serial, card.name ?? "Товар", card.price.rent.find((x) => x != null) ?? null)}
        />
      )}
      {tab === "service" && <Service onPick={(id, name) => add({ kind: "service", service: id, name, price: null })} />}
      {entries.length > 0 && (
        <div className="flex flex-col gap-1" data-annot="выбрано в договор">
          <div className="font-medium">Выбрано: {entries.length}</div>
          <ul className="flex flex-col gap-1">
            {entries.map(({ key, item }) => (
              <li key={key} className="flex flex-wrap items-center gap-2 rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1">
                <span className="min-w-0 flex-1 break-words">
                  {item.kind === "serial" ? (
                    <>
                      {item.name} · <span className="font-mono">{item.serial}</span>
                    </>
                  ) : (
                    <>
                      {item.name} <span className="text-fg-muted">· услуга</span>
                    </>
                  )}
                </span>
                <label className="flex items-center gap-1">
                  <input
                    className={`${numCls} w-24`}
                    inputMode="numeric"
                    aria-label="Цена, ₽"
                    placeholder="цена"
                    value={item.price ?? ""}
                    onChange={(e) => patch(key, { price: num(e.target.value, PRICE_MAX) })}
                  />
                  ₽
                </label>
                {item.kind === "serial" && (
                  <label className="flex items-center gap-1">
                    <input
                      className={`${numCls} w-14`}
                      inputMode="numeric"
                      aria-label="Дней проката"
                      placeholder="—"
                      value={item.days ?? ""}
                      onChange={(e) => patch(key, { days: num(e.target.value, DAYS_MAX) })}
                    />
                    дн.
                  </label>
                )}
                <button type="button" onClick={() => remove(key)} className="grid size-6 place-items-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg" aria-label="Убрать" title="Убрать">
                  <X size={14} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </fieldset>
  );
}
